<script lang="ts" setup>
import { ref, toRefs, onMounted, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Panel from 'primevue/panel';
import { useToast } from 'primevue/usetoast';
import TreeSelect from 'primevue/treeselect';
import { EMIT } from '@/enum';
import { OrganizationInterface, StudentTableInterface } from '@/types';
import { useOrganizationStore } from '@/store';
import Icon from '@/components/Icon';

interface TransferOrgPropsInterface {
  students: StudentTableInterface[] | [];
  organization: OrganizationInterface;
}

interface TransferOrgPayloadInterface {
  students: number[] | [];
  organization: OrganizationInterface;
}

const toast = useToast();
const organizationStoreParents = useOrganizationStore();
const organizationStore = useOrganizationStore();

const props = defineProps<TransferOrgPropsInterface>();
const emit = defineEmits<{
  (e: EMIT.CANCEL): void;
  (e: EMIT.TRANSFER, payload: TransferOrgPayloadInterface): void;
}>();

const { students, organization } = toRefs(props);
const studentNames = computed(() => {
  return students.value
    .map((student) => {
      return student.name;
    })
    .join(', ');
});
const organizations = ref();
const idSelectedOrganization = ref();
const selectedOrganization = ref<OrganizationInterface>();
const modalVisible = ref(true);

onMounted(async () => {
  await organizationStore.getOrganizations();
  await organizationStoreParents.getParentsOrganizations();
  organizations.value = organizationStore.organizations.data
    .map((org) => org.data)
    .filter((org) => org.id !== organization.value.id);
});

const selectOrganization = async () => {
  const organizationId = Number(Object.keys(idSelectedOrganization.value)[0]);
  selectedOrganization.value = await organizationStore.getOrganizationInfo(organizationId);
  if (selectedOrganization.value.id === organizationStore.organization.id) {
    toast.add({
      severity: 'warn',
      summary: 'Not selectable',
      detail: 'Cannot be transferred to the same organization',
      life: 3000,
    });
    selectedOrganization.value = {} as OrganizationInterface;
    idSelectedOrganization.value = {};
  }
};

const transferOrg = (): void => {
  emit(EMIT.TRANSFER, {
    students: students.value.map((student) => student.id),
    organization: selectedOrganization.value as OrganizationInterface,
  });
  selectedOrganization.value = undefined;
  modalVisible.value = false;
};

const cancel = (): void => {
  emit(EMIT.CANCEL);
  selectedOrganization.value = undefined;
  modalVisible.value = false;
};
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    header="Transfer to new organization"
    :draggable="false"
    :modal="true"
    class="dialog__container transfer__modal"
    @hide="cancel"
  >
    <div class="field col">
      <label class="required" for="type">Organization</label>
      <TreeSelect
        id="organization_type_id"
        v-model="idSelectedOrganization"
        :options="organizationStoreParents.organizationParents"
        option-label="name"
        selection-mode="single"
        option-value="id"
        :loading="organizationStoreParents.loading"
        placeholder="Select Organization"
        style="font-weight: bold"
        class="w-full"
        @change="selectOrganization"
      />
    </div>
    <div class="field col">
      <Panel class="preview__container">
        <template #header>
          <div class="flex flex-column p-2 w-full preview__text">Preview</div>
          <div class="flex flex-row p-2 w-full m-0 transfer__items">{{ studentNames }}</div>
        </template>
        <div class="flex flex-row w-full m-0 pl-2 org__items">
          {{ organization.name }}
          <Icon class="pl-3 pr-3" name="arrow_forward" />
          <span class="selected__org" :class="{ selected: selectedOrganization }">
            {{ selectedOrganization?.name || 'New Organization' }}
          </span>
        </div>
      </Panel>
      <div class="flex flex-row mt-4 align-content-center">
        <Icon name="warning" class="md-12 warning__text__icon mr-1" />
        <span class="warning__text__span text-xs">
          Any connections to users that do not belong to the new organization will be removed.
        </span>
      </div>
    </div>
    <template #footer>
      <AppButton text="Cancel" type="secondary" @click="cancel" />
      <AppButton :disabled="!selectedOrganization" text="Transfer Student(s)" type="primary" @click="transferOrg" />
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
.dialog__container {
  :deep(.p-dialog) {
    .p-dialog-header {
      padding-bottom: 0.5rem !important;
    }
  }
  .preview__container {
    :deep(.p-panel-header) {
      padding: 0;
      background: $white;
      flex-direction: column;
      border-bottom: 2px solid $grayLight;
      .preview__text {
        font-weight: 600;
      }
      .transfer__items {
        font-size: 14px;
      }
    }
    :deep(.p-panel-content) {
      padding: 0.5rem 0;
      font-size: 14px;
      color: $grayDark;
      .org__items {
        .selected__org {
          font-weight: 600;
          color: $grayMedium;
          &.selected {
            color: $grayDark;
          }
        }
        i {
          color: $grayMedium;
        }
      }
      .warning__text__icon {
        color: $grayDark;
      }
      .warning__text__span {
        margin-top: 2px;
      }
    }
  }
}
</style>
